<template>
  <div class="modal-wrapper">
    <div
      class="modal-content"
      :style="{
        '--minContentWidth': minContentWidth,
        '--maxContentWidth': maxContentWidth,
        '--minContentHeight': minContentHeight,
        '--maxContentHeight': maxContentHeight
      }"
    >
      <header>
        <h2 class="lead-text">{{ modalTitle }}</h2>

        <VButton
          type="button"
          class="close-btn"
          ui-type="transparent"
          icon-name="close"
          :icon-size="24"
          sr-title="close modal"
          @click="hide"
        />
      </header>

      <article class="basic-text">
        <slot></slot>
      </article>

      <footer v-if="pattern === 'btn2'">
        <VButton
          type="button"
          class="btn2-left"
          :ui-type="btn2Left?.uiType ? btn2Left?.uiType : 'secondary'"
          :title="btn2Left?.title"
          :disabled="btn2Left?.disabled"
          @click="btn2Left?.action"
        />
        <VButton
          type="button"
          class="btn2-right"
          :ui-type="btn2Right?.uiType ? btn2Right?.uiType : 'danger'"
          :title="btn2Right?.title"
          :disabled="btn2Right?.disabled"
          :status="btn2Right?.status"
          @click="btn2Right?.action"
        />
      </footer>
      <footer v-else>
        <VButton type="button" class="btn1" :ui-type="btnUiType" :title="btnTitle" @click="btnClicked" />
      </footer>
    </div>
    <div class="window-overlay" @click="hide" />
  </div>
</template>

<script lang="ts">
export type VSelectOption = { value: string; label: string; color?: string; disabled?: boolean };
import { PropType, defineComponent } from "vue";
import VButton from "@/components/atoms/v-button.vue";

export default defineComponent({
  name: "component-dialog",
  components: { VButton },
  props: {
    modalTitle: String,
    btnTitle: {
      type: String,
      default: "OK"
    },
    btnAction: Function,
    btnUiType: {
      type: String,
      default: "primary"
    },
    minContentWidth: { type: String, default: "332px" },
    maxContentWidth: { type: String, default: "800px" },
    minContentHeight: { type: String, default: "165px" },
    maxContentHeight: { type: String, default: "90vh" },
    pattern: {
      type: String,
      required: false,
      validator: (value: string) => ["btn2"].includes(value)
    },
    btn2Left: {
      type: Object as PropType<{
        title: string;
        action: () => void;
        disabled?: boolean;
        uiType?: string;
      }>,
      required: false
    },
    btn2Right: {
      type: Object as PropType<{
        title: string;
        action: () => void;
        disabled?: boolean;
        uiType?: string;
        status?: string;
      }>,
      required: false
    }
  },

  setup(props, { emit }) {
    const methods = {
      hide() {
        emit("hide");
      },
      btnClicked() {
        if (!props.btnAction) methods.hide();
        else {
          props.btnAction();
        }
      }
    };

    return {
      ...methods
    };
  }
});
</script>

<style lang="scss" scoped>
$overlay-z-index: 9998;
$dialog-z-index: 9999;
$minContentWidth: var(--minContentWidth);
$maxContentWidth: var(--maxContentWidth);
$minContentHeight: var(--minContentHeight);
$maxContentHeight: var(--maxContentHeight);

$dialog-z-index: 9999;
$overlay-z-index: 9998;

.modal-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $overlay-z-index;

  .modal-content {
    position: relative;
    margin: auto 5vw;
    padding-top: 32px;
    min-height: $minContentHeight;
    max-height: $maxContentHeight;
    min-width: $minContentWidth;
    max-width: $maxContentWidth;
    box-sizing: border-box;
    border-radius: 12px;
    background-color: white;
    z-index: $dialog-z-index;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media screen and (max-width: 800px) {
      max-width: 80vw;
      width: auto;
      margin: unset;
    }
    @media screen and (max-width: 375px) {
      max-width: 80vw;
    }

    header {
      margin-bottom: 16px;
      padding: 0 32px;

      .close-btn {
        padding: 0;
        margin: 0;
        width: 24px;
        height: 24px;
        position: absolute;
        right: 16px;
        top: 8px;
      }
    }

    article {
      width: calc(100% - 64px);
      height: calc(100% - 60px);
      word-break: break-all;
      overflow-y: auto;
      margin-bottom: 32px;
      padding: 0 32px;
    }

    footer {
      background-color: white;
      height: calc(60px - 12px);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px 16px;
      border-top: 1px solid $north-snow;

      button {
        width: 140px;
        height: 48px;
        margin-right: 24px;
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  .window-overlay {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100vw;
    height: 100vh;
    background-color: rgba(black, 0.4);
    z-index: $overlay-z-index;
  }
}
</style>
